import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { profileBounds, profileError } from '@/lib/clearance/profile';
import type { ClearanceVertex } from '@/lib/clearance/types';

export interface ClearanceProfileSketchProps {
  vertices: readonly ClearanceVertex[];
  /** Shown on the datum label; the sketch's own axes are offset/height. */
  verticalOffsetM: number;
  widthPx?: number;
  heightPx?: number;
  /** One point drawn on the polygon — the dock's worst-intrusion marker. */
  highlight?: { offsetM: number; heightM: number } | null;
}

const PAD = 28;
/** How close a clamped guide line may sit to the canvas edge. */
const EDGE = 8;

/**
 * The live 2D sketch of a clearance profile, with axes and stated dimensions.
 *
 * Its whole job is to make a feet/metres mistake visible before the run: a
 * "14" that meant feet draws a box four times too tall next to a stated
 * "4.27 m", and the reader sees it in the shape, not in the number. So the
 * width and height are PRINTED, not just drawn — a scale-free outline would
 * look identical either way.
 *
 * Pure SVG, no state, no refs: it re-renders from props on every keystroke of
 * the editor, which is what makes it live.
 */
export const ClearanceProfileSketch = ({
  vertices,
  verticalOffsetM,
  widthPx = 260,
  heightPx = 200,
  highlight = null,
}: ClearanceProfileSketchProps) => {
  const { t } = useTranslation('project');
  const invalid = profileError(vertices);

  const view = useMemo(() => {
    if (vertices.length < 3) return null;
    const b = profileBounds(vertices);
    const w = Math.max(b.maxOffset - b.minOffset, 1e-6);
    const h = Math.max(b.maxHeight - b.minHeight, 1e-6);
    // One scale for both axes: a profile drawn with independent x/y scales is
    // a lie about its shape, which is the one thing this view exists to show.
    const scale = Math.min((widthPx - 2 * PAD) / w, (heightPx - 2 * PAD) / h);
    const cx = (b.minOffset + b.maxOffset) / 2;
    const cy = (b.minHeight + b.maxHeight) / 2;
    const toPx = ([o, hh]: ClearanceVertex): [number, number] => [
      widthPx / 2 + (o - cx) * scale,
      // SVG y grows downward; height grows upward.
      heightPx / 2 - (hh - cy) * scale,
    ];
    // The datum and the centreline can fall outside the box whenever the
    // profile does not straddle them — a rectangle with a 4 m base puts the
    // datum 87 px below the canvas, taking the offset READOUT with it, which
    // is exactly the profile whose datum is least obvious. Clamp both guides
    // to the edge and report that they were clamped, so the label survives and
    // an arrow says which way the real zero lies.
    const rawZeroY = heightPx / 2 + cy * scale;
    const rawZeroX = widthPx / 2 - cx * scale;
    const zeroY = Math.min(Math.max(rawZeroY, EDGE), heightPx - EDGE);
    const zeroX = Math.min(Math.max(rawZeroX, EDGE), widthPx - EDGE);
    return {
      w,
      h,
      toPx,
      zeroY,
      zeroX,
      // Below the canvas means the datum is below everything drawn, so the
      // arrow points down.
      datumArrow: rawZeroY > zeroY ? '↓ ' : rawZeroY < zeroY ? '↑ ' : '',
      centreClamped: rawZeroX !== zeroX,
    };
  }, [vertices, widthPx, heightPx]);

  if (!view || invalid) {
    // Deliberately the neutral prompt even when `invalid` holds a sentence:
    // the editor prints that sentence directly below this box, and a 260 px
    // wide sketch is the wrong place to say the same thing twice.
    return (
      <div
        data-testid='clearance-sketch-empty'
        className='flex items-center justify-center rounded border border-dashed border-slate-300 p-4 text-center text-xs text-slate-500 dark:border-stone-700 dark:text-stone-400'
        style={{ width: widthPx, height: heightPx }}>
        {t('clearance.sketch.empty')}
      </div>
    );
  }

  const pixels = vertices.map((v) => view.toPx(v));
  const points = pixels.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const hi = highlight ? view.toPx([highlight.offsetM, highlight.heightM]) : null;

  return (
    <svg
      width={widthPx}
      height={heightPx}
      role='img'
      aria-label={t('clearance.sketch.alt', { width: view.w.toFixed(2), height: view.h.toFixed(2) })}
      className='rounded border border-slate-200 bg-white dark:border-stone-700 dark:bg-stone-900'>
      {/* Datum (height = 0) and centreline (offset = 0): the two references
          every vertex is measured from, drawn so the reader can see which way
          a negative height or a left offset goes. */}
      <line
        x1={0}
        x2={widthPx}
        y1={view.zeroY}
        y2={view.zeroY}
        stroke='currentColor'
        strokeOpacity={0.35}
        strokeDasharray='4 3'
      />
      <line
        x1={view.zeroX}
        x2={view.zeroX}
        y1={0}
        y2={heightPx}
        stroke='currentColor'
        strokeOpacity={view.centreClamped ? 0.1 : 0.2}
        strokeDasharray='4 3'
      />
      <polygon
        data-testid='clearance-sketch-polygon'
        points={points}
        fill='rgb(56 189 248 / 0.18)'
        stroke='rgb(2 132 199)'
        strokeWidth={1.5}
        strokeLinejoin='round'
      />
      {pixels.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={2.5} fill='rgb(2 132 199)' />
      ))}
      {hi && (
        <g data-testid='clearance-sketch-highlight'>
          <circle cx={hi[0]} cy={hi[1]} r={4.5} fill='none' stroke='rgb(220 38 38)' strokeWidth={2} />
          <circle cx={hi[0]} cy={hi[1]} r={1.5} fill='rgb(220 38 38)' />
        </g>
      )}
      <text
        data-testid='clearance-sketch-width'
        x={widthPx / 2}
        y={heightPx - 6}
        textAnchor='middle'
        fontSize={10}
        fill='currentColor'>
        {t('clearance.sketch.width', { value: view.w.toFixed(2) })}
      </text>
      <text data-testid='clearance-sketch-height' x={6} y={12} fontSize={10} fill='currentColor'>
        {t('clearance.sketch.height', { value: view.h.toFixed(2) })}
      </text>
      <text
        data-testid='clearance-sketch-datum'
        x={widthPx - 6}
        y={view.zeroY - 4}
        textAnchor='end'
        fontSize={10}
        fill='currentColor'
        opacity={0.7}>
        {view.datumArrow}
        {t('clearance.sketch.datum', { value: verticalOffsetM.toFixed(2) })}
      </text>
    </svg>
  );
};
